import axios from 'axios';
import _get from 'lodash/get';
import to from 'await-to-js';
import cookies from 'js-cookie';
import _forEach from 'lodash/forEach';
import { isOptimusMode } from '@libs/utils';
import Hashes from 'jshashes';
import dayjs from 'dayjs';
import dayjsUTC from 'dayjs/plugin/utc';
import dayjsTimezone from 'dayjs/plugin/timezone';

dayjs.extend(dayjsUTC);
dayjs.extend(dayjsTimezone);

// import { generateFingerPrint, isFingerPrintEnabled } from '@libs/fingerprint';

import qms from './qms';
import locale from './locale';
import account from './account';
import discovery from './discovery';
import appointment from './appointment';
import config from '@root/config';
import { OkaHMAC, refreshXDate, getTimeDiffFromCookie } from 'okadoc-libs';

const publicSecret = ['p3z4l','f4124h','k3n41200k'];
const randomId = Math.floor(Math.random() * publicSecret.length);
const kongHMAC = new OkaHMAC(config.KONG_SERVICES_CONFIG);

const defaultConfig = {
    headers: {
        'Content-Type': 'application/json'
    }
};

class OKA {
    static done = false;
    static label = {
        token: 'OKA.token',
        user: 'OKA.user'
    }
    static url = {
        account: {
            url: "https://service.okadoc.co/account/v1"
        },
        appointment: {
            url: "https://service.okadoc.co/appointment/v1"
        },
        discovery: {
            url: "https://service.okadoc.co/discovery/v1"
        },
        notification: {
            url: "https://service.okadoc.co/notification/v1"
        },
        qms: {
            url: "https://service.okadoc.co/qms/v1/"
        },
        locale: {
            url: "https://service.okadoc.co/locale/v1"
        }
    }
    static http = {
        account: false,
        appointment: false,
        discovery: false,
        notification: false,
        qms: false,
        locale: false
    };

    static discovery = discovery

    static appointment = appointment;

    static account = account;

    static qms = qms;

    static locale = locale;

    static init = async(config, clientId) => {
        if (OKA.done) return
        OKA.done = true;
        if (config) {
            OKA.url = {
                ...OKA.url,
                ...config
            }
            OKA.clientId = clientId;
        }

        // create each servive
        
        _forEach(OKA.http, (request, key) => {
            if (OKA.url[key] !== undefined) {
            
                OKA.http[key] = axios.create({
                    ...defaultConfig,
                    baseURL: `${OKA.url[key].url.replace(/\/?$/, '')}`,
                    headers: {
                        ...defaultConfig.headers,
                        'Accept-Language': 'id'
                    }
                });
            }
            OKA.http[key].interceptors.request.use(conf => {
                const token = cookies.get(OKA.label.token);
                if (OKA.clientId !== "") {
                    conf.headers['Client-ID'] = OKA.clientId;
                }
                if (token && token.token) {
                    conf.headers.Authorization = `Bearer ${token.token}`;
                }
                if(isOptimusMode()) {
                    conf.headers['Optimus'] = true;
                }

                const d = new Date();
                const str = OKA.clientId+d.getUTCHours()+d.getUTCMinutes()+d.getUTCSeconds()+publicSecret[randomId]
                const xToken = new Hashes.SHA256().hex_hmac('f4124hp354l',str);

                conf.headers['X-API-Token'] = xToken;
                conf.headers['x-api-time'] = d.toISOString();
                conf.headers['timezone'] = dayjs.tz.guess();

                // Generates and set request finger print ...
                // if (isFingerPrintEnabled()) {
                //     const fingerPrint = generateFingerPrint(conf);
                //     conf.headers['X-API-Key'] = fingerPrint;
                // }

                conf.name = key;
                // Gets the `time-difference` value from cookie only
                // skipping from `calling-api`
                const timeDiff = getTimeDiffFromCookie({
                    skipGetServerTime: true,
                    isServer: false
                });
                conf.timeDiff = timeDiff;

                // updating `kong-hmac` headers
                kongHMAC.updateHeaders(conf);

                return conf;
            }, (error) => {
                return Promise.reject(error);
            });

            OKA.http[key].interceptors.response.use(
                res => res,
                errorRes => {
                    const status = _get(errorRes, 'response.status');
                    const message = _get(errorRes, 'response.data.message');

                    // refresh HMAC headers when the `X-Date` field is invalid
                    // due to incorrect user's local Datetime.
                    if (status === 406 && message === 'Unauthorised') {
                        const { config: prevConfig } = errorRes;

                        return refreshXDate(prevConfig, updatedConfig => {
                            // re-updating `kong-hmac` headers
                            // when `refreshing-xdate` done.
                            kongHMAC.updateHeaders(updatedConfig);
                        });
                    }


                    if (axios.isCancel(errorRes)) {
                        return false;
                    }

                    if (errorRes.message === 'Network Error') {
                        return Promise.reject(errorRes);
                    }

                    if (!errorRes.response) {
                        return Promise.reject(errorRes);
                    }

                    return Promise.reject(errorRes.response);
                }
            );
        });
        if (typeof window !== 'undefined') {
            window.OKAPI = OKA;
        }

        return OKA;
    }

    static getLoginStatus = () => {
        if (cookies.get(OKA.label.token) !== undefined && cookies.get(OKA.label.token) !== '') {
            return {
                connected: true,
                status: 'connected'
            };
        }

        return {
            connected: false,
            status: 'disconected'
        };
    }

    static initialize = () => new Promise(async(resolve, reject) => {
        if (OKA.getLoginStatus()) {
            const response = await to(OKA.http.account.get('/account/v1/auth/me'));

            if(response[0]) {
                cookies.remove(OKA.label.token);
                reject(response[0]);
            }
            const data = _get(response, 'data');
            cookies.set(OKA.label.user, data, { expires: 1 });
            resolve(data);
        }
    })
}

export default OKA;